import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CircleUserRound, LogOut, Menu, Settings, X } from "lucide-react";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useEnsureUser } from "@/hooks/useEnsureUser";
import { cn } from "@/lib/cn";

export interface SecondaryNavItem {
  to: string;
  label: string;
  activePrefixes?: string[];
}

interface SiteHeaderProps {
  secondaryLabel?: string;
  secondaryNav?: SecondaryNavItem[];
}

const PRIMARY_NAV = [
  { to: "/news", label: "News", activePrefix: "/news" },
  { to: "/ladder/predictor", label: "Ladder", activePrefix: "/ladder" },
  { to: "/tipping/dashboard", label: "Tipping", activePrefix: "/tipping" },
];

export function BrandMark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="font-display inline-flex items-baseline font-black uppercase leading-none tracking-[0.08em] text-white">
      <span className={compact ? "text-xl" : "text-2xl"}>Tackle</span>
      <span className={cn("text-primary", compact ? "text-xl" : "text-2xl")}>Four</span>
      <span className="ml-1.5 h-1.5 w-1.5 -translate-y-2 rounded-full bg-ice" />
    </span>
  );
}

export function SiteHeader({ secondaryLabel, secondaryNav }: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const { data: currentUser } = useEnsureUser();
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  const isPrimaryActive = (item: (typeof PRIMARY_NAV)[number]) =>
    pathname.startsWith(item.activePrefix);

  const signIn = () => loginWithRedirect({ appState: { returnTo: "/profile" } });

  return (
    <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-navy-950/95 backdrop-blur-xl">
      <a
        href="#main-content"
        className="fixed left-3 top-3 z-[100] -translate-y-20 rounded-md bg-primary px-4 py-2 text-sm font-bold text-white focus:translate-y-0"
      >
        Skip to content
      </a>

      <div className="mx-auto flex h-16 max-w-[1440px] items-center px-5 sm:px-8 lg:px-12">
        <Link to="/" aria-label="Tackle Four home" className="shrink-0">
          <BrandMark />
        </Link>

        <nav className="ml-7 hidden self-stretch md:flex" aria-label="Primary navigation">
          {PRIMARY_NAV.map((item) => {
            const active = isPrimaryActive(item);
            return (
              <Link
                key={item.label}
                to={item.to}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex items-center px-3 text-sm font-semibold text-white/55 transition-colors hover:text-white",
                  active && "text-white",
                )}
              >
                {item.label}
                {active && <span className="absolute inset-x-3 bottom-0 h-0.5 bg-primary" />}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto hidden items-center gap-2 md:flex">
          {currentUser?.isAdmin && (
            <Link
              to="/admin"
              aria-label="Admin"
              className="grid h-9 w-9 place-items-center rounded-md text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <Settings className="h-4 w-4" />
            </Link>
          )}
          {isAuthenticated ? (
            <Link
              to="/profile"
              className={cn(
                "flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-bold transition-colors",
                pathname === "/profile"
                  ? "border-primary/70 bg-primary/10 text-white"
                  : "border-white/15 text-white/75 hover:border-primary/70 hover:bg-primary/10 hover:text-white",
              )}
            >
              <UserAvatar
                avatarUrl={currentUser?.avatarUrl}
                userId={currentUser?.id ?? user?.sub ?? ""}
                displayName={currentUser?.displayName ?? user?.name ?? ""}
                size="sm"
                className="h-5 w-5"
              />
              Profile
            </Link>
          ) : (
            <button
              type="button"
              onClick={signIn}
              className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-2 text-sm font-bold text-white/75 transition-colors hover:border-primary/70 hover:bg-primary/10 hover:text-white"
            >
              <CircleUserRound className="h-4 w-4" />
              Sign in
            </button>
          )}
          {isAuthenticated && (
            <button
              type="button"
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              aria-label="Log out"
              title="Log out"
              className="grid h-9 w-9 place-items-center rounded-md text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          className="ml-auto grid h-10 w-10 place-items-center rounded-md border border-white/10 text-white md:hidden"
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Close navigation" : "Open navigation"}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-white/[0.07] bg-navy-950 px-5 pb-5 pt-3 md:hidden">
          <nav className="grid gap-1" aria-label="Mobile navigation">
            {PRIMARY_NAV.map((item) => {
              const active = isPrimaryActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "rounded-md px-3 py-3 text-base font-semibold text-white/60",
                    active && "bg-white/[0.06] text-white",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
            {isAuthenticated ? (
              <Link
                to="/profile"
                className={cn(
                  "rounded-md px-3 py-3 text-base font-semibold text-white/60",
                  pathname === "/profile" && "bg-white/[0.06] text-white",
                )}
              >
                Profile
              </Link>
            ) : (
              <button
                type="button"
                onClick={signIn}
                className="rounded-md px-3 py-3 text-left text-base font-semibold text-white/60"
              >
                Sign in
              </button>
            )}
            {currentUser?.isAdmin && (
              <Link
                to="/admin"
                className="rounded-md px-3 py-3 text-base font-semibold text-white/60"
              >
                Admin
              </Link>
            )}
            {isAuthenticated && (
              <button
                type="button"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                className="mt-2 flex items-center justify-between rounded-md border border-white/10 px-3 py-3 text-left text-base font-semibold text-white/60"
              >
                Log out
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </nav>
        </div>
      )}

      {secondaryNav && secondaryNav.length > 0 && (
        <div className="border-t border-white/[0.07] bg-navy-900/95">
          <nav
            className="mx-auto flex max-w-[1440px] gap-1 overflow-x-auto px-5 sm:px-8 lg:px-12"
            aria-label={secondaryLabel ?? "Section navigation"}
          >
            {secondaryNav.map((item) => {
              const active = (item.activePrefixes ?? [item.to]).some(
                (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
              );
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "shrink-0 border-b-2 border-transparent px-3 py-3 text-sm font-semibold text-white/50 transition-colors hover:text-white",
                    active && "border-primary text-white",
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
